/**
 * One-page day sheet type scale.
 * pageHeight is in any unit (mm or px); returned sizes use the same unit.
 */

export const DAY_SHEET_PAGE_MM = { width: 210, height: 297 };
export const DAY_SHEET_ASPECT = DAY_SHEET_PAGE_MM.width / DAY_SHEET_PAGE_MM.height;

export function mmToPt(mm) {
  return (Number(mm) || 0) * (72 / 25.4);
}

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

/**
 * Size tour title, date box, and cue rows so they fill exactly one page.
 * Few cues → larger type. Many cues → smaller type. Never taller than the page.
 *
 * @param {{
 *   pageHeight: number,
 *   rowCount: number,
 *   hasBanner?: boolean,
 *   hasAftershow?: boolean,
 * }} opts
 */
export function daySheetTypeScale({
  pageHeight,
  rowCount,
  hasBanner = false,
  hasAftershow = false,
}) {
  const h = Math.max(1, Number(pageHeight) || DAY_SHEET_PAGE_MM.height);
  const n = Math.max(1, Math.floor(Number(rowCount) || 1));

  const padTop = h * 0.052;
  const padBottom = h * 0.048;
  let bannerH = hasBanner ? h * 0.12 : 0;
  let titleH = h * 0.115;
  let infoH = h * 0.15;
  let afterH = hasAftershow ? h * 0.065 : 0;

  const minCue = h * 0.013;
  const maxCue = h * 0.046;
  const cueRatio = 0.56;

  const chrome = () => padTop + padBottom + bannerH + titleH + infoH + afterH;

  let available = h - chrome();
  let rowH = available / n;
  let cue = rowH * cueRatio;

  if (cue > maxCue) {
    const targetRows = (maxCue / cueRatio) * n;
    const extra = Math.max(0, available - targetRows);
    titleH += extra * 0.48;
    infoH += extra * 0.36;
    if (hasBanner) bannerH += extra * 0.16;
    cue = maxCue;
    rowH = maxCue / cueRatio;
  }

  available = h - chrome();
  rowH = available / n;
  cue = rowH * cueRatio;

  if (cue < minCue) {
    const need = (minCue / cueRatio) * n;
    const flexible = bannerH + titleH + infoH + afterH;
    const maxFlex = Math.max(h * 0.16, h - padTop - padBottom - need);
    if (flexible > 0 && maxFlex < flexible) {
      const k = maxFlex / flexible;
      bannerH *= k;
      titleH *= k;
      infoH *= k;
      afterH *= k;
    }
    available = h - chrome();
    rowH = available / n;
    cue = Math.max(h * 0.0085, rowH * cueRatio);
  }

  const used = chrome() + rowH * n;
  const fit = used > h ? h / used : 1;

  const out = {
    padTop: padTop * fit,
    padBottom: padBottom * fit,
    bannerH: bannerH * fit,
    titleH: titleH * fit,
    infoH: infoH * fit,
    afterH: afterH * fit,
    rowH: rowH * fit,
    cueSize: cue * fit,
    titleSize: clamp(titleH * fit * 0.62, h * 0.018, h * 0.085),
    infoSize: clamp(infoH * fit * 0.26, h * 0.014, h * 0.032),
    afterSize: clamp(afterH * fit * 0.38, h * 0.012, h * 0.028),
  };
  out.usedHeight = out.padTop + out.padBottom + out.bannerH + out.titleH + out.infoH + out.afterH + out.rowH * n;
  return out;
}

/** Largest A4 page that fits in a box (preview = PDF page). */
export function daySheetPageBox(availW, availH) {
  const w = Math.max(0, Number(availW) || 0);
  const h = Math.max(0, Number(availH) || 0);
  if (!w || !h) {
    return { width: 0, height: 0 };
  }
  const byWidth = { width: w, height: w / DAY_SHEET_ASPECT };
  if (byWidth.height <= h) return byWidth;
  return { width: h * DAY_SHEET_ASPECT, height: h };
}
